<template>
    <div>
        <div v-if="$refs.grid" v-show="false" v-text="$refs.grid.filters"></div>
        <b-datatable
            ref="grid"
            :key="dataTableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-sort="initialSort"
            :initial-filters="initialFilters"
            class="p-0"
            has-filter-selector
            item-key="id"
            :grid-title="gridTitle"
            :keep-filters="false"
            notification
            @onReset="onReset"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip v-if="can('horiba-samples.download')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                :disabled="exportCsvDisabled"
                                color="primary"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(exportCsvUrl())"
                            >
                                <v-icon>mdi-printer</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('quality-control::button.export_csv') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template #filters="{ filters }">
                <v-col cols="4">
                    <b-filter-date
                        v-model="filters.filters.filter_created_at"
                        range
                        :label="t('quality-control::filters.label.date')"
                        @clear="onCloseDateFilter(filters, 'filter_created_at')"
                        @input="setGroupDate"
                    ></b-filter-date>
                </v-col>
            </template>

            <template #[`item.is_passed`]="{ item }">
                <v-icon v-if="!item.is_passed" color="red">mdi-alert-octagon-outline</v-icon>
            </template>

            <template #[`item.user.name`]="{ item }">
                <span>{{ item.user !== null ? item.user.name : t('default::label.na') }}</span>
            </template>

            <template #[`item.created_at`]="{ item }">
                {{
                    item.created_at ? moment(item.created_at).format($root.dateFormatWithHours) : t('default::label.na')
                }}
            </template>

            <template #[`item.setting_name`]="{ item }">
                <div class="d-flex">
                    <div class="pr-2">
                        <span
                            :class="item.is_passed ? '' : 'red--text'"
                            class="link"
                            @click="openDialog(item.id, item.setting_name)"
                            >{{ item.setting_name }}</span
                        >
                    </div>
                    <div>
                        <div class="grid-status d-inline ml-2">
                            <span>
                                {{
                                    item.is_passed
                                        ? t('quality-control::grid.label.passed')
                                        : t('quality-control::grid.label.failed')
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
        </b-datatable>
        <v-dialog v-if="detailsDialog" v-model="detailsDialog" max-width="950px" @input="closeDetailsDialog">
            <probe-data-table
                :headers="viewProbeHeaders"
                :items="viewProbeItems"
                :table-title="tableTitle"
                :table-sub-title="tableSubTitle"
                :is-passed="selectedBloodResult.is_passed"
            ></probe-data-table>
        </v-dialog>
    </div>
</template>

<script>
import ProbeDataTable from './ProbeDataTable.vue';

export default {
    name: 'HoribaSamplesGroupDataTable',
    components: { ProbeDataTable },
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            dataTableKey: 0,
            initialSort: '-created_at',
            hiddenColumns: ['id'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    width: 55,
                    align: 'center',
                    sortable: false,
                },
                {
                    text: null,
                    value: 'is_passed',
                    width: 55,
                    align: 'center',
                    sortable: false,
                },
                {
                    text: t('quality-control::grid.label.operator'),
                    value: 'user.name',
                    width: 160,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.date'),
                    value: 'created_at',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.lot'),
                    value: 'setting_name',
                    sortable: true,
                    width: 200,
                },
            ],
            viewProbeHeaders: [
                {
                    text: t('quality-control::grid.label.parameter'),
                    value: 'parameter',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.unit'),
                    value: 'unit',
                    width: 80,
                    sortable: false,
                },
                {
                    text: t('quality-control::grid.label.control'),
                    value: 'control',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.tolerance'),
                    value: 'tolerance',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.probe'),
                    value: 'probe',
                    width: 80,
                    sortable: true,
                },
            ],
            detailsDialog: false,
            selectedBloodResult: {},
            groupDate: null,
        };
    },
    computed: {
        gridTitle() {
            if (this.horibaSamplesProvider && this.horibaSamplesProvider.name) {
                if (
                    this.initialFilters.created_at &&
                    moment(this.initialFilters.created_at).format(this.$root.dateFormat)
                ) {
                    if (this.groupDate && this.groupDate.length > 0) {
                        if (this.groupDate.length === 1) {
                            return `${this.horibaSamplesProvider.name}
                                <small class="def">${t('quality-control::grid.title.samples_from')}</small>&nbsp;
                                ${moment(this.groupDate[0]).format(this.$root.dateFormat)}
                            `;
                        }
                        if (this.groupDate.length === 2) {
                            let dates = {
                                start_date: null,
                                end_date: null,
                            };
                            if (moment(this.groupDate[0]).isAfter(moment(this.groupDate[1]))) {
                                dates.start_date = this.groupDate[1];
                                dates.end_date = this.groupDate[0];
                            }
                            if (moment(this.groupDate[1]).isAfter(moment(this.groupDate[0]))) {
                                dates.start_date = this.groupDate[0];
                                dates.end_date = this.groupDate[1];
                            }
                            return `${this.horibaSamplesProvider.name}
                                <small class="def">${t('quality-control::grid.title.samples_from')}</small>&nbsp;
                                ${moment(dates.start_date).format(this.$root.dateFormat)}
                                <small class="def">${t('quality-control::grid.title.samples_to')}</small>&nbsp;
                                ${moment(dates.end_date).format(this.$root.dateFormat)}
                            `;
                        }
                    }
                    return `${this.horibaSamplesProvider.name}
                                <small class="def">${t('quality-control::grid.title.samples_from')}</small>&nbsp;
                                ${moment(this.initialFilters.created_at).format(this.$root.dateFormat)}
                            `;
                }
                return this.horibaSamplesProvider.name;
            }

            return '';
        },
        horibaSamples() {
            return this.$store.state.qualityControl.horiba_samples;
        },
        horibaSamplesProvider() {
            return this.horibaSamples.provider;
        },
        viewProbeItems() {
            let items = new Array();
            this.selectedBloodResult.sample_properties.forEach((item) => {
                const mapItem = new Object();
                if (item.horiba_setting) {
                    mapItem.parameter = item.horiba_setting.property;
                    mapItem.unit = item.horiba_setting.unit;
                    mapItem.control = item.horiba_setting.value;
                    mapItem.tolerance = item.horiba_setting.tolerance;
                    mapItem.probe = item.value;
                    mapItem.isPassed = item.is_passed;
                } else {
                    mapItem.parameter = item.property;
                    mapItem.unit = item.unit;
                    mapItem.control = null;
                    mapItem.tolerance = null;
                    mapItem.probe = item.value;
                    mapItem.isPassed = item.isPassed;
                    mapItem.min = item.min;
                    mapItem.max = item.max;
                }

                items.push(mapItem);
            });
            return items;
        },
        tableTitle() {
            return `${this.selectedBloodResult.setting_name} - ${this.selectedBloodResult.type} QC`;
        },
        tableSubTitle() {
            return `${
                this.selectedBloodResult.is_passed
                    ? t('quality-control::grid.label.passed')
                    : t('quality-control::grid.label.failed')
            } - ${this.selectedBloodResult.setting_name} (${moment(this.selectedBloodResult.created_at).format(
                this.$root.dateFormat
            )}) - ${this.selectedBloodResult.type} QC!`;
        },
        exportCsvDisabled() {
            return this.$store.state.data_table.items && !this.$store.state.data_table.items.length > 0;
        },
    },
    mounted() {
        this.setFilters();
    },
    methods: {
        onReset() {
            this.groupDate = [this.initialFilters.created_at];
            _.delay(() => this.setFilters(true), 400);
        },
        setFilters(fromReset = false) {
            if (this.initialFilters.created_at && this.$refs.grid) {
                this.$refs.grid.filters.created_at = this.dateFilterConverter([this.initialFilters.created_at]);
                this.$refs.grid.filters.filter_created_at = [this.initialFilters.created_at];
                if (fromReset) {
                    this.$refs.grid.filters.created_at = this.groupDate;
                    this.$refs.grid.filters.created_at = this.dateFilterConverter(this.groupDate);
                    this.$refs.grid.filters.filter_created_at = this.groupDate;
                }
            }
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'filter_created_at':
                    filters.filters.created_at = null;
                    filters.filters.filter_created_at = null;
                    break;
                default:
                    break;
            }
        },
        openDialog(id, name) {
            let url = name.startsWith('PIX') ? 'api.v1.pixcell-samples.show' : 'api.v1.horiba-samples.show';
            let fields = name.startsWith('PIX') ? '*' : '*,sample_properties.*,sample_properties.horiba_setting.*';
            let params = {
                fields: fields,
            };
            params[name.startsWith('PIX') ? 'pixcell_sample' : 'horiba_sample'] = id;
            axios({
                method: 'get',
                url: route(url, params),
            })
                .then((result) => {
                    this.selectedBloodResult = result.data.data;
                    this.detailsDialog = true;
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                    this.detailsDialog = false;
                });
        },
        closeDetailsDialog() {
            this.detailsDialog = false;
            this.selectedBloodResult = {};
        },
        setGroupDate(value) {
            this.groupDate = value;
        },
        dateFilterConverter(val) {
            let dates = _.clone(val);
            if (!dates) {
                return null;
            }
            if (dates && dates.length <= 1) {
                return `${val[0]}`;
            }
            dates[0] = `gte:${val[0]}`;
            dates[1] = `lte:${val[1]}`;
            if (moment(val[0]).isAfter(moment(val[1]))) {
                dates[0] = `gte:${val[1]}`;
                dates[1] = `lte:${val[0]}`;
            }
            if (moment(val[1]).isAfter(moment(val[0]))) {
                dates[0] = `gte:${val[0]}`;
                dates[1] = `lte:${val[1]}`;
            }
            return dates.join(',');
        },
        exportCsvUrl() {
            let urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('created_at')) {
                this.createdAt = urlParams.get('created_at');
            }
            const options = { created_at: this.createdAt, provider_id: this.initialFilters.provider_id };
            if (this.groupDate) {
                delete options.created_at;
                options.created_at = this.dateFilterConverter(this.groupDate);
            }
            return route('horiba-samples.download', options);
        },
    },
};
</script>

<style></style>
